import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TenantExportResponse } from '../../../models/tenant-export';
import { ExportService } from '../../../services/export.service';

const POLL_INTERVAL_MS = 5000;

@Component({
  standalone: false,
  selector: 'iwt-estatecloud-tenant-export',
  templateUrl: './tenant-export.component.html',
  styleUrls: ['./tenant-export.component.scss'],
})
export class TenantExportComponent implements OnInit, OnDestroy {
  @Input({ required: true }) customerId!: string;

  exports: TenantExportResponse[] = [];
  includeFiles = true;
  isLoading = false;
  isStarting = false;
  isCancelling = false;
  errorMessage = '';
  fileModalExport: TenantExportResponse | undefined;
  downloadingFile: string | undefined;
  showAllExports = false;

  private pollTimer: ReturnType<typeof setInterval> | undefined;

  constructor(private readonly exportService: ExportService) {}

  ngOnInit(): void {
    void this.loadExports();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  get hasRunningExport(): boolean {
    return this.exports.some((exportJob) => exportJob.status === 'RUNNING');
  }

  // exports arrive sorted by startedDate descending — [0] is the latest
  get visibleExports(): TenantExportResponse[] {
    return this.showAllExports ? this.exports : this.exports.slice(0, 1);
  }

  get hiddenExportCount(): number {
    return Math.max(0, this.exports.length - 1);
  }

  async startExport(): Promise<void> {
    this.errorMessage = '';
    this.isStarting = true;
    try {
      await this.exportService.startExport(this.customerId, {
        includeFiles: this.includeFiles,
      });
      await this.loadExports();
    } catch (error) {
      this.errorMessage = toMessage(error);
    } finally {
      this.isStarting = false;
    }
  }

  onIncludeFilesChange(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.includeFiles = event.target.checked;
    }
  }

  async cancelExport(exportJob: TenantExportResponse): Promise<void> {
    this.errorMessage = '';
    this.isCancelling = true;
    try {
      await this.exportService.cancelExport(
        this.customerId,
        exportJob.exportId,
      );
      await this.loadExports();
    } catch (error) {
      this.errorMessage = toMessage(error);
    } finally {
      this.isCancelling = false;
    }
  }

  openFilesModal(exportJob: TenantExportResponse): void {
    this.fileModalExport = exportJob;
  }

  closeFilesModal(): void {
    this.fileModalExport = undefined;
  }

  async downloadFile(
    exportJob: TenantExportResponse,
    fileName: string,
  ): Promise<void> {
    this.errorMessage = '';
    this.downloadingFile = fileName;
    try {
      const download = await this.exportService.getDownloadUrl(
        this.customerId,
        exportJob.exportId,
        fileName,
      );
      const url = download.files[0]?.url;
      if (!url) {
        throw new Error(`No download url for ${fileName}`);
      }
      triggerDownload(url);
    } catch (error) {
      this.errorMessage = toMessage(error);
    } finally {
      this.downloadingFile = undefined;
    }
  }

  badgeVariant(exportJob: TenantExportResponse): string {
    if (exportJob.status === 'SUCCEEDED') {
      return 'success';
    }
    if (exportJob.status === 'FAILED') {
      return 'error';
    }
    if (exportJob.status === 'CANCELLED') {
      return 'gray';
    }
    return 'info';
  }

  formatSize(bytes: number | undefined): string {
    if (!bytes) {
      return '';
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = bytes;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }
    return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
  }

  private async loadExports(): Promise<void> {
    this.isLoading = true;
    try {
      const response = await this.exportService.getExports(this.customerId);
      this.exports = response.data;
      this.syncPolling();
    } catch (error) {
      this.errorMessage = toMessage(error);
    } finally {
      this.isLoading = false;
    }
  }

  private syncPolling(): void {
    if (this.hasRunningExport && this.pollTimer === undefined) {
      this.pollTimer = setInterval(() => void this.refresh(), POLL_INTERVAL_MS);
    }
    if (!this.hasRunningExport) {
      this.stopPolling();
    }
  }

  private async refresh(): Promise<void> {
    try {
      const response = await this.exportService.getExports(this.customerId);
      this.exports = response.data;
      this.syncPolling();
    } catch {
      // polling errors are transient — keep the last known state
    }
  }

  private stopPolling(): void {
    if (this.pollTimer !== undefined) {
      clearInterval(this.pollTimer);
      this.pollTimer = undefined;
    }
  }
}

function toMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function triggerDownload(url: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

